import { useState, useEffect } from 'react';
import LimitEditor from './LimitEditor';
import EyePanel from './EyePanel';
import {
  getSettings, putSettings, getModels, toggleModel, type AdminModel, type AdminSettings,
  getPricing, putPricing, type AdminPricing,
  getOverview, getCustomers, getInvoices, markInvoicePaid, setCustomerLimits,
  updateInvoice, sendInvoice, deleteInvoice, deleteCustomer, grantPro, revokeCustomer,
  getActivity, adminLogin, getCustomerScanRuns, getCustomerScanRunDetail, getCustomerRunsComparison,
  type AdminOverview, type AdminCustomer, type AdminInvoice, type ActivityEvent, type ActivitySummary,
  type AdminSettingsSnapshot, type CustomerLimitPatch,
} from './adminApi';
import ScanHistory from '../components/ScanHistory';

const eur = (cents: number): string => `€${(cents / 100).toFixed(2)}`;
const date = (s: string | null): string => (s ? new Date(s).toLocaleDateString() : 'n/a');
const dt = (s: string | null): string => (s ? new Date(s).toLocaleString() : 'n/a');
const dur = (secs: number): string => {
  const s = Math.round(secs);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};

type Tab = 'overview' | 'activity' | 'customers' | 'invoices' | 'config' | 'models';
const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'activity', label: 'Activity' },
  { id: 'customers', label: 'Customers' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'config', label: 'Config' },
  { id: 'models', label: 'Models' },
];

// Slovak explanations for every tunable setting (admin-only screen).
const SETTING_HELP: Record<string, { label: string; help: string }> = {
  anonRateLimitPerDay: { label: 'Free, skenov za deň', help: 'Koľko readiness skenov (URL → skóre) denne zvládne FREE prihlásený účet. Počíta sa per účet. Pro účty tento limit nemajú.' },
  anonProbeLimitPerDay: { label: 'Free, probov za deň', help: 'Denný limit visibility + footprint testov pre FREE účet. Súčet cez oba enginy (Gemini aj GPT dokopy). Per účet, nezávisle od IP.' },
  proProbePerDay: { label: 'Pro, probov za deň', help: 'Denný limit visibility + footprint testov pre PRO účet. Súčet cez oba enginy, per účet (nezávisle od IP).' },
  proProbePerMonth: { label: 'Pro, probov za mesiac', help: 'Mesačný fair-use strop probov pre PRO účet (súčet cez oba enginy, per účet). Chráni pred zneužitím pri neobmedzenom dennom počte.' },
  dailyCostCapUsd: { label: 'Denný cost strop (USD)', help: 'Keď celkové denné náklady na AI presiahnu túto sumu, drahé LLM časti sa vypnú: skeny bežia bez LLM extrakcie, proby sa pozastavia do polnoci. Poistka rozpočtu.' },
  freeMonthlyQuota: { label: 'Free, mesačná kvóta (nepoužité)', help: 'Zatiaľ sa nikde nepoužíva, rezerva do budúcna. Reálne limity riešia polia vyššie.' },
  visibilityProbeSamples: { label: 'Vzoriek na otázku', help: 'Koľkokrát sa každá otázka spýta AI. AI odpovedá zakaždým trochu inak, preto vzorkujeme viackrát a berieme väčšinový výsledok. Viac = presnejšie, ale drahšie a pomalšie.' },
  visibilityQuestions: { label: 'Visibility, počet otázok (Pro)', help: 'Koľko otázok sa vygeneruje pre visibility probe u PRO účtu. Free je vždy zastropený na 5 (natvrdo, FREE_PROBE_QUESTIONS).' },
  footprintQuestions: { label: 'Footprint, počet otázok (Pro)', help: 'Koľko otázok pre footprint u PRO účtu. Free je vždy zastropený na 5.' },
  cacheTtlHoursA: { label: 'Cache readiness (hodiny)', help: 'Ako dlho sa drží uložený výsledok readiness skenu tej istej URL, aby sa opakovaný sken nepočítal a neplatil. Napr. 24 = jeden deň.' },
  cacheTtlHoursB: { label: 'Cache probe (hodiny)', help: 'Ako dlho sa drží uložený výsledok visibility/footprint pre tú istú URL.' },
  notifyOperatorEveryNTests: { label: 'Notifikácia tebe, každých N testov', help: 'Pošle TEBE (operátorovi) email po každých N testoch celkovo. 1 = po každom teste, 10 = po každom desiatom. 0 = vypnuté.' },
  clientDailyTestLimit: { label: 'Notifikácia klientovi, po N testoch/deň', help: 'Keď klient spraví toľkoto testov za deň, pošle sa MU upozorňujúci email. Nie je to tvrdý limit (ten je vyššie), len notifikácia.' },
};

/** Spend is what WE pay providers, in dollars — the old view formatted it as
 *  euros next to revenue figures, which read as income. */
const usd = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

export default function AdminApp(): JSX.Element {
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [settings, setSettings] = useState<AdminSettings>({});
  const [pricing, setPricing] = useState<AdminPricing | null>(null);
  const [pricingSaved, setPricingSaved] = useState(false);
  const [models, setModels] = useState<AdminModel[]>([]);
  const [saved, setSaved] = useState(false);
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [limitDefaults, setLimitDefaults] = useState<AdminSettingsSnapshot | null>(null);
  const [editLimits, setEditLimits] = useState<AdminCustomer | null>(null);
  /** Clients → [klient] → AI EYE (D-36). */
  const [eyeFor, setEyeFor] = useState<{ id: string; email: string } | null>(null);
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [activitySummary, setActivitySummary] = useState<ActivitySummary[]>([]);
  const [sortDesc, setSortDesc] = useState(true);

  // Per-customer scan history overlay (#5).
  const [viewCustomer, setViewCustomer] = useState<{ id: string; email: string } | null>(null);

  // Inline delete confirmation (native confirm() is unreliable in some browsers).
  const [confirmDel, setConfirmDel] = useState<{ kind: 'customer' | 'invoice'; id: string } | null>(null);
  const isConfirming = (kind: 'customer' | 'invoice', id: string): boolean => confirmDel?.kind === kind && confirmDel.id === id;

  // Inline invoice editor state.
  const [editId, setEditId] = useState<string | null>(null);
  const [editNumber, setEditNumber] = useState('');
  const [editAmount, setEditAmount] = useState('');

  async function reloadCommerce(): Promise<void> {
    const [o, c, i, a] = await Promise.all([getOverview(token), getCustomers(token), getInvoices(token), getActivity(token)]);
    setOverview(o); setCustomers(c.customers); setLimitDefaults(c.settings); setInvoices(i);
    setEvents(a.events); setActivitySummary(a.summary);
  }
  function guard<T extends unknown[]>(fn: (...a: T) => Promise<void>) {
    return async (...a: T): Promise<void> => {
      try { await fn(...a); await reloadCommerce(); }
      catch (e) { setError(e instanceof Error ? e.message : 'action failed'); }
    };
  }
  const onMarkPaid = guard((id: string) => markInvoicePaid(token, id).then(() => {}));
  const onSend = guard((id: string) => sendInvoice(token, id).then(() => {}));
  const onDeleteInvoice = guard((id: string) => deleteInvoice(token, id).then(() => {}));
  const onDeleteCustomer = guard((id: string) => deleteCustomer(token, id).then(() => {}));
  const onRevoke = guard((id: string) => revokeCustomer(token, id).then(() => {}));

  // Manual comp grant (give Pro to an email without payment).
  const [grantEmail, setGrantEmail] = useState('');
  const [grantMonths, setGrantMonths] = useState('12');
  const onGrant = guard(async () => {
    await grantPro(token, grantEmail.trim(), Number(grantMonths) || 12);
    setGrantEmail('');
  });

  function startEdit(i: AdminInvoice): void {
    setEditId(i.id); setEditNumber(i.invoice_number ?? ''); setEditAmount((i.amount_cents / 100).toFixed(2));
  }
  const saveEdit = guard(async (id: string) => {
    await updateInvoice(token, id, {
      invoiceNumber: editNumber.trim() || null,
      amountCents: Math.round(parseFloat(editAmount || '0') * 100),
    });
    setEditId(null);
  });

  // Load all admin data with a token (shared by login + session restore).
  async function loadAll(t: string): Promise<void> {
    const [s, pr, m, o, c, i, a] = await Promise.all([
      getSettings(t), getPricing(t), getModels(t), getOverview(t), getCustomers(t), getInvoices(t), getActivity(t),
    ]);
    setToken(t); setSettings(s); setPricing(pr); setModels(m);
    setOverview(o); setCustomers(c.customers); setLimitDefaults(c.settings); setInvoices(i); setEvents(a.events); setActivitySummary(a.summary);
    setAuthed(true);
  }

  // Restore a persisted session on load so a refresh doesn't log you out (#2).
  useEffect(() => {
    const saved = (() => { try { return localStorage.getItem('aivs_admin_token'); } catch { return null; } })();
    if (saved) loadAll(saved).catch(() => { try { localStorage.removeItem('aivs_admin_token'); } catch { /* ignore */ } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login (email + password → 2FA code), then load everything with the token.
  async function login(creds: { email?: string; password?: string; code?: string } = {}): Promise<void> {
    if (loggingIn) return;                 // a second click must not fire a second login
    setError(null);
    setLoggingIn(true);
    try {
      // Read the fields from the form, not from React state: a browser autofill
      // can populate the inputs without firing onChange, which left the state
      // empty and made the button look dead.
      const r = await adminLogin(
        (creds.email || email).trim(),
        creds.password || password,
        needTotp ? (creds.code || code).trim() : undefined,
      );
      if ('totpRequired' in r) { setNeedTotp(true); return; } // ask for the 2FA code next
      try { localStorage.setItem('aivs_admin_token', r.token); } catch { /* ignore */ }
      await loadAll(r.token);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'login failed');
    } finally {
      setLoggingIn(false);
    }
  }

  function logout(): void {
    try { localStorage.removeItem('aivs_admin_token'); } catch { /* ignore */ }
    setToken(''); setAuthed(false); setNeedTotp(false); setPassword(''); setCode('');
  }
  async function save(): Promise<void> {
    setSaved(false);
    try { const res = await putSettings(token, settings); setSettings(res.settings); setSaved(true); }
    catch (e) { setError(e instanceof Error ? e.message : 'save failed'); }
  }
  async function savePricing(): Promise<void> {
    if (!pricing) return;
    setPricingSaved(false);
    try { const res = await putPricing(token, pricing); setPricing(res); setPricingSaved(true); }
    catch (e) { setError(e instanceof Error ? e.message : 'save failed'); }
  }
  // Bind a euro <input> to a *_cents field in the pricing object.
  const setCents = (key: 'monthlyCents' | 'annualCents', euro: string): void =>
    setPricing((p) => (p ? { ...p, [key]: Math.max(0, Math.round(parseFloat(euro || '0') * 100)) } : p));
  const setPromo = (patch: Partial<AdminPricing['promo']>): void =>
    setPricing((p) => (p ? { ...p, promo: { ...p.promo, ...patch } } : p));
  async function onToggle(m: AdminModel): Promise<void> {
    try {
      await toggleModel(token, m.id, !m.enabled);
      setModels((prev) => prev.map((x) => (x.id === m.id ? { ...x, enabled: !x.enabled } : x)));
    } catch (e) { setError(e instanceof Error ? e.message : 'toggle failed'); }
  }

  const byDate = <T extends { created_at: string }>(rows: T[]): T[] =>
    [...rows].sort((a, b) => (sortDesc ? 1 : -1) * (new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));

  if (!authed) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-3 px-6">
        <h1 className="font-serif text-3xl">AIO Scan · Admin</h1>
        <p className="font-mono text-sm text-text-secondary">
          {needTotp ? 'Enter your 2FA code (same authenticator as RAG Chat).' : 'Sign in with your email and password (same as RAG Chat).'}
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void login({
              email: String(f.get('email') ?? ''),
              password: String(f.get('password') ?? ''),
              code: String(f.get('code') ?? ''),
            });
          }}
          className="flex flex-col gap-2 w-72"
        >
          {!needTotp ? (
            <>
              <input name="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" autoFocus
                className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded-lg px-4 py-2 text-text-primary outline-none font-mono text-sm" />
              <input name="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password"
                className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded-lg px-4 py-2 text-text-primary outline-none font-mono text-sm" />
            </>
          ) : (
            <input name="code" type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123 456" autoFocus
              className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded-lg px-4 py-2 text-text-primary outline-none font-mono text-lg tracking-widest text-center" />
          )}
          <button type="submit" disabled={loggingIn}
            className="bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2 text-sm disabled:opacity-60">
            {loggingIn ? 'Checking…' : needTotp ? 'Verify code' : 'Continue'}
          </button>
        </form>
        {error && <p className="font-mono text-sm text-[#E0574A]">{error}</p>}
        <p className="font-mono text-sm text-text-secondary/60 mt-2">Access is limited to allowed IPs.</p>
      </main>
    );
  }

  const SortToggle = (): JSX.Element => (
    <button onClick={() => setSortDesc((d) => !d)} className="font-mono text-sm text-text-secondary hover:text-cyan transition">
      Date {sortDesc ? '↓ newest' : '↑ oldest'}
    </button>
  );

  return (
    <main className="min-h-screen px-6 py-10 flex flex-col items-center gap-6">
      <div className="flex items-center gap-4">
        <h1 className="font-serif text-3xl">AIO Scan · Admin</h1>
        <button onClick={logout} className="font-mono text-sm text-text-secondary hover:text-cyan transition">Sign out</button>
      </div>
      {error && <p className="font-mono text-sm text-[#E0574A]">{error}</p>}

      {/* Per-customer scan history as a page, not a modal (replaces the tabs). */}
      {viewCustomer ? (
        <section className="w-full max-w-4xl">
          <button onClick={() => setViewCustomer(null)} className="font-mono text-sm text-text-secondary hover:text-cyan transition">← back to customers</button>
          <div className="mt-3 mb-5">
            <h2 className="font-serif text-2xl">Scans &amp; progress</h2>
            <p className="text-sm text-text-secondary">{viewCustomer.email}</p>
          </div>
          <ScanHistory
            fetcher={({ page, domain, engine, from, to }) => getCustomerScanRuns(token, viewCustomer.id, { page, domain, engine, from, to })}
            detailFetcher={(runId) => getCustomerScanRunDetail(token, viewCustomer.id, runId)}
            comparisonFetcher={(domain) => getCustomerRunsComparison(token, viewCustomer.id, domain)}
            viewer={{ email: viewCustomer.email }}
          />
        </section>
      ) : (
      <>
      {/* Tabs (like the RAG Chat admin) */}
      <div className="flex flex-wrap gap-1 border border-warm-dark-2 rounded-lg p-1">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`font-mono text-sm px-4 py-1.5 rounded transition ${tab === t.id ? 'bg-cyan text-warm-dark' : 'text-text-secondary hover:text-text-primary'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && overview && (
        <section className="w-full max-w-4xl grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ['Customers', String(overview.customers)],
            ['Pro (active)', String(overview.pro)],
            ['Runs (mo)', String(overview.scans)],
            ['Probes (mo)', String(overview.probes)],
            ['Spend (mo)', usd(overview.cost_cents)],
            ['Runs today', String(overview.runs_today)],
            ['Spend today', `${usd(overview.spent_today_cents)} / $${overview.capUsd}`],
            ['Auto re-checks on', String(overview.schedules)],
          ].map(([label, val]) => (
            <div key={label} className="border border-warm-dark-2 rounded-xl p-4 text-center">
              <div className="font-mono text-2xl text-cyan">{val}</div>
              <div className="font-mono text-sm text-text-secondary mt-1">{label}</div>
            </div>
          ))}
        </section>
      )}

      {tab === 'activity' && (
        <>
          <section className="w-full max-w-4xl border border-warm-dark-2 rounded-xl p-5">
            <h2 className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-3">Per-user summary</h2>
            {activitySummary.length === 0 ? (
              <p className="font-mono text-sm text-text-secondary">No users yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-text-secondary font-mono text-sm text-left">
                    <th className="py-1 pr-3">Email</th><th className="pr-3">Tests</th><th className="pr-3">Last test</th><th className="pr-3">Sessions</th><th className="pr-3">Time logged in</th><th className="pr-3">Last seen</th>
                  </tr></thead>
                  <tbody>
                    {activitySummary.map((s) => (
                      <tr key={s.email} className="border-t border-warm-dark-2">
                        <td className="py-2 pr-3">{s.email}</td>
                        <td className="pr-3 font-mono text-cyan">{s.tests}</td>
                        <td className="pr-3 font-mono text-text-secondary">{dt(s.last_test)}</td>
                        <td className="pr-3 font-mono">{s.sessions}</td>
                        <td className="pr-3 font-mono">{dur(s.logged_seconds)}</td>
                        <td className="pr-3 font-mono text-text-secondary">{dt(s.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="w-full max-w-4xl border border-warm-dark-2 rounded-xl p-5">
            <h2 className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-3">Recent tests</h2>
            {events.length === 0 ? (
              <p className="font-mono text-sm text-text-secondary">No tests yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-text-secondary font-mono text-sm text-left">
                    <th className="py-1 pr-3">When</th><th className="pr-3">Who</th><th className="pr-3">Domain</th><th className="pr-3">Kind</th><th className="pr-3">IP</th>
                  </tr></thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id} className="border-t border-warm-dark-2">
                        <td className="py-2 pr-3 font-mono text-text-secondary">{dt(e.created_at)}</td>
                        <td className="pr-3">{e.email ?? <span className="text-text-secondary">anonymous</span>}</td>
                        <td className="pr-3 font-mono text-cyan">{e.domain || e.url}</td>
                        <td className="pr-3 font-mono text-text-secondary">{e.kind}</td>
                        <td className="pr-3 font-mono text-text-secondary">{e.ip ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="font-mono text-sm text-text-secondary mt-3">Latest 200 tests. "Time logged in" sums each session from sign-in to last activity.</p>
          </section>
        </>
      )}

      {tab === 'invoices' && (
        <section className="w-full max-w-4xl border border-warm-dark-2 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-mono text-sm uppercase tracking-widest text-text-secondary">Orders / invoices</h2>
            <SortToggle />
          </div>
          {invoices.length === 0 ? (
            <p className="font-mono text-sm text-text-secondary">No orders yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-text-secondary font-mono text-sm text-left">
                  <th className="py-1 pr-3">#</th><th className="pr-3">Date</th><th className="pr-3">Email</th><th className="pr-3">Invoice no.</th><th className="pr-3">Cycle</th><th className="pr-3">Amount</th><th className="pr-3">Status</th><th></th>
                </tr></thead>
                <tbody>
                  {byDate(invoices).map((i) => (
                    <tr key={i.id} className="border-t border-warm-dark-2 align-top">
                      <td className="py-2 pr-3 font-mono">{i.id}</td>
                      <td className="pr-3 font-mono text-text-secondary">{date(i.created_at)}</td>
                      <td className="pr-3">{i.email}</td>
                      {editId === i.id ? (
                        <>
                          <td className="pr-3"><input value={editNumber} onChange={(e) => setEditNumber(e.target.value)} placeholder="e.g. 42026"
                            className="w-24 bg-warm-dark-2 border border-cyan/40 rounded px-2 py-1 font-mono text-sm outline-none" /></td>
                          <td className="pr-3 font-mono">{i.billing_cycle}</td>
                          <td className="pr-3"><input value={editAmount} onChange={(e) => setEditAmount(e.target.value)}
                            className="w-16 bg-warm-dark-2 border border-cyan/40 rounded px-2 py-1 font-mono text-sm outline-none" /></td>
                          <td className="pr-3 font-mono">{i.status}</td>
                          <td className="text-right whitespace-nowrap">
                            <button onClick={() => void saveEdit(i.id)} className="font-mono text-sm text-cyan hover:underline mr-2">Save</button>
                            <button onClick={() => setEditId(null)} className="font-mono text-sm text-text-secondary hover:underline">Cancel</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="pr-3 font-mono">{i.invoice_number ?? '·'}</td>
                          <td className="pr-3 font-mono">{i.billing_cycle}</td>
                          <td className="pr-3 font-mono">{eur(i.amount_cents)}</td>
                          <td className="pr-3 font-mono"><span className={i.status === 'paid' ? 'text-measured' : 'text-estimated'}>{i.status}</span></td>
                          <td className="text-right whitespace-nowrap">
                            <button onClick={() => startEdit(i)} className="font-mono text-sm text-text-secondary hover:text-cyan mr-2">Edit</button>
                            <button onClick={() => void onSend(i.id)} className="font-mono text-sm text-text-secondary hover:text-cyan mr-2">{i.status === 'requested' ? 'Send' : 'Resend'}</button>
                            {i.status !== 'paid' && (
                              <button onClick={() => void onMarkPaid(i.id)} className="font-mono text-sm bg-cyan text-warm-dark rounded px-2 py-0.5 hover:opacity-90 mr-2">Mark paid</button>
                            )}
                            {isConfirming('invoice', i.id) ? (
                              <>
                                <button onClick={() => { setConfirmDel(null); void onDeleteInvoice(i.id); }} className="font-mono text-sm text-[#E0574A] hover:underline mr-1">confirm</button>
                                <button onClick={() => setConfirmDel(null)} className="font-mono text-sm text-text-secondary hover:underline">cancel</button>
                              </>
                            ) : (
                              <button onClick={() => setConfirmDel({ kind: 'invoice', id: i.id })} className="font-mono text-sm text-[#E0574A] hover:underline">Delete</button>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="font-mono text-sm text-text-secondary mt-3">
            An order records intent. Set your real invoice number (from your accounting), Send emails the customer, Mark paid activates Pro. No variable symbol is invented here.
          </p>
        </section>
      )}

      {/* Per-customer controls (D-32). Most accounts here are the operator's own
          test accounts, and metering them against a paying customer's allowance
          is what locked the product out of its own testing. */}
      {editLimits && (
        <LimitEditor
          c={editLimits}
          defaults={limitDefaults}
          onClose={() => setEditLimits(null)}
          onSave={guard(async (patch: CustomerLimitPatch) => {
            await setCustomerLimits(token, editLimits.id, patch);
            setEditLimits(null);
          })}
        />
      )}

      {/* Párovací kód, widgety, importy a spárované stránky — jedno miesto,
          lebo klient v plugine nenastavuje nič (D-36 §3). */}
      {eyeFor && (
        <EyePanel
          token={token}
          customerId={eyeFor.id}
          email={eyeFor.email}
          onClose={() => setEyeFor(null)}
        />
      )}

      {tab === 'customers' && (
        <section className="w-full max-w-4xl border border-warm-dark-2 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-mono text-sm uppercase tracking-widest text-text-secondary">Customers &amp; usage</h2>
            <SortToggle />
          </div>

          {/* Manually grant Pro to an email (comp, no payment). They then just sign in. */}
          <div className="border border-cyan/30 rounded-lg p-3 mb-4 bg-cyan/5">
            <p className="text-sm text-text-primary mb-2">Grant Pro to an email (no payment)</p>
            <div className="flex flex-wrap items-center gap-2">
              <input type="email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="person@company.com"
                className="flex-1 min-w-[12rem] bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-3 py-1.5 font-mono text-sm outline-none" />
              <input type="number" value={grantMonths} onChange={(e) => setGrantMonths(e.target.value)} title="months"
                className="w-16 bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1.5 font-mono text-sm outline-none" />
              <span className="font-mono text-sm text-text-secondary">months</span>
              <button disabled={!grantEmail.trim()} onClick={() => void onGrant()}
                className="font-mono text-sm bg-cyan text-warm-dark rounded px-4 py-1.5 disabled:opacity-50 hover:opacity-90 transition">
                Grant Pro
              </button>
            </div>
            <p className="font-mono text-sm text-text-secondary mt-2">
              Tell them to open the app, click "Sign in", enter this email, and use the code we email them. They then have Pro.
            </p>
          </div>

          {customers.length === 0 ? (
            <p className="font-mono text-sm text-text-secondary">No customers yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-text-secondary font-mono text-sm text-left">
                  <th className="py-1 pr-3">Email</th><th className="pr-3">Tier</th>
                  <th className="pr-3" title="probe runs used today / this month, against the account's limit">Used</th>
                  <th className="pr-3" title="scan runs this month (lifetime)">Runs</th>
                  <th className="pr-3" title="visibility + footprint probes this month (lifetime)">Probes</th>
                  <th className="pr-3" title="real spend attributed to this customer this month">Cost (mo)</th>
                  <th className="pr-3" title="tests recorded / time signed in">Activity</th>
                  <th className="pr-3">Last run</th><th></th>
                </tr></thead>
                <tbody>
                  {byDate(customers).map((c) => (
                    <tr key={c.id} className="border-t border-warm-dark-2">
                      <td className="py-2 pr-3">
                        <button onClick={() => setViewCustomer({ id: c.id, email: c.email })} className="text-cyan hover:underline">{c.email}</button>
                        <div className="font-mono text-xs text-text-secondary/70">
                          joined {date(c.created_at)}{c.note ? ` · ${c.note}` : ''}
                          {' · '}
                          <button onClick={() => setEyeFor({ id: c.id, email: c.email })} className="text-cyan/80 hover:text-cyan hover:underline">AI EYE</button>
                        </div>
                      </td>
                      <td className="pr-3 font-mono">
                        <button onClick={() => setEditLimits(c)} className={c.tier === 'pro' ? 'text-success hover:underline' : 'text-text-secondary hover:underline'}>
                          {c.tier}
                        </button>
                        {c.status !== 'active' && <div className="font-mono text-xs text-estimated">{c.status}</div>}
                      </td>
                      {/* What the gate has actually counted, against this account's
                          own limit. The number that decides whether they are locked out. */}
                      <td className="pr-3 font-mono">
                        {c.limits_unlimited ? (
                          <span className="text-success">unlimited</span>
                        ) : (
                          <span className={c.used_month >= (c.probe_per_month ?? limitDefaults?.proProbePerMonth ?? 0) ? 'text-[#E0574A]' : ''}>
                            {c.used_today}/{c.probe_per_day ?? limitDefaults?.proProbePerDay ?? '-'} d
                            <span className="text-text-secondary/60"> · </span>
                            {c.used_month}/{c.probe_per_month ?? limitDefaults?.proProbePerMonth ?? '-'} m
                          </span>
                        )}
                        {(c.probe_per_day != null || c.probe_per_month != null) && (
                          <div className="font-mono text-xs text-estimated">custom</div>
                        )}
                      </td>
                      <td className="pr-3 font-mono">{c.scans}<span className="text-text-secondary/50"> ({c.scans_total})</span></td>
                      <td className="pr-3 font-mono">{c.probes}<span className="text-text-secondary/50"> ({c.probes_total})</span></td>
                      <td className="pr-3 font-mono">{eur(c.cost_cents)}</td>
                      <td className="pr-3 font-mono text-text-secondary">
                        {c.tests} tests
                        <div className="text-xs text-text-secondary/60">{Math.round(Number(c.logged_seconds) / 60)} min · {c.sessions} sess.</div>
                      </td>
                      <td className="pr-3 font-mono text-text-secondary">{date(c.last_run)}</td>
                      <td className="text-right whitespace-nowrap">
                        {c.tier === 'pro'
                          ? <button onClick={() => void onRevoke(c.id)} className="font-mono text-sm text-estimated hover:underline mr-2">Revoke Pro</button>
                          : <button onClick={() => void grantPro(token, c.email, 12).then(reloadCommerce).catch((e) => setError(e instanceof Error ? e.message : 'grant failed'))} className="font-mono text-sm text-cyan hover:underline mr-2">Make Pro</button>}
                        {isConfirming('customer', c.id) ? (
                          <>
                            <span className="text-sm text-text-secondary mr-1">Delete all data?</span>
                            <button onClick={() => { setConfirmDel(null); void onDeleteCustomer(c.id); }} className="font-mono text-sm text-[#E0574A] hover:underline mr-2">yes</button>
                            <button onClick={() => setConfirmDel(null)} className="font-mono text-sm text-text-secondary hover:underline">no</button>
                          </>
                        ) : (
                          <button onClick={() => setConfirmDel({ kind: 'customer', id: c.id })} className="font-mono text-sm text-[#E0574A] hover:underline">Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === 'config' && pricing && (
        <section className="w-full max-w-4xl border border-cyan/30 rounded-xl p-5 mb-5 bg-cyan/5">
          <h2 className="font-mono text-sm uppercase tracking-widest text-cyan mb-1">Cena Pro &amp; akcia</h2>
          <p className="text-sm text-text-secondary mb-4">Verejná cena Pro. Zobrazuje sa na Pricing stránke, v objednávke aj v upsell hláškach. Akcia = badge + poznámka pri cene; dá sa vypnúť.</p>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <label className="flex flex-col gap-1 text-sm text-text-secondary">
              <span className="text-text-primary">Mesačne (€)</span>
              <input type="number" step="0.01" min="0" value={pricing.monthlyCents / 100}
                onChange={(e) => setCents('monthlyCents', e.target.value)}
                className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none font-mono text-sm" />
            </label>
            <label className="flex flex-col gap-1 text-sm text-text-secondary">
              <span className="text-text-primary">Ročne (€)</span>
              <input type="number" step="0.01" min="0" value={pricing.annualCents / 100}
                onChange={(e) => setCents('annualCents', e.target.value)}
                className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none font-mono text-sm" />
            </label>
            <label className="flex items-center gap-2 text-sm text-text-secondary md:col-span-2">
              <input type="checkbox" checked={pricing.promo.active} onChange={(e) => setPromo({ active: e.target.checked })} className="accent-cyan" />
              <span className="text-text-primary">Akcia zapnutá</span>
              <span className="text-xs text-text-secondary">(keď vypneš, badge aj poznámka zmiznú)</span>
            </label>
            <label className="flex flex-col gap-1 text-sm text-text-secondary">
              <span className="text-text-primary">Badge text</span>
              <input type="text" maxLength={40} value={pricing.promo.badge} onChange={(e) => setPromo({ badge: e.target.value })}
                placeholder="Early access"
                className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none font-mono text-sm" />
            </label>
            <label className="flex flex-col gap-1 text-sm text-text-secondary">
              <span className="text-text-primary">Poznámka pri cene</span>
              <input type="text" maxLength={200} value={pricing.promo.note} onChange={(e) => setPromo({ note: e.target.value })}
                placeholder="Early-access price. It stays yours…"
                className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none text-sm" />
            </label>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button onClick={() => void savePricing()} className="bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2 text-sm">Save price</button>
            {pricingSaved && <span className="font-mono text-sm text-measured">saved ✓</span>}
          </div>
        </section>
      )}

      {tab === 'config' && (
        <section className="w-full max-w-4xl border border-warm-dark-2 rounded-xl p-5">
          <h2 className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-1">Limity &amp; nastavenia</h2>
          <p className="text-sm text-text-secondary mb-4">Uložené hodnoty prepisujú env defaulty. Limity sa počítajú <span className="text-text-primary">per účet</span> a sú <span className="text-text-primary">súčtom cez oba enginy</span>.</p>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            {Object.entries(settings).map(([k, v]) => {
              const h = SETTING_HELP[k];
              return (
                <label key={k} className="flex flex-col gap-1 text-sm text-text-secondary">
                  <span className="text-text-primary">{h?.label ?? k}</span>
                  <input type="number" value={v} onChange={(e) => setSettings((s) => ({ ...s, [k]: Number(e.target.value) }))}
                    className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none font-mono text-sm" />
                  {h && <span className="text-xs leading-snug text-text-secondary">{h.help}</span>}
                  <span className="font-mono text-[10px] text-text-secondary/50">{k}</span>
                </label>
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button onClick={save} className="bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2 text-sm">Save</button>
            {saved && <span className="font-mono text-sm text-measured">saved ✓</span>}
          </div>
        </section>
      )}

      {tab === 'models' && (
        <section className="w-full max-w-xl border border-warm-dark-2 rounded-xl p-5">
          <h2 className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-3">Model registry (D-02)</h2>
          <div className="flex flex-col gap-1">
            {models.map((m) => (
              <label key={m.id} className="flex items-center gap-3 py-1.5 border-t border-warm-dark-2 text-sm cursor-pointer">
                <input type="checkbox" checked={m.enabled} onChange={() => onToggle(m)} className="accent-cyan" />
                <span className="font-mono text-sm text-text-secondary w-16">{m.kind}</span>
                <span className="text-text-primary">{m.provider} / {m.name}</span>
              </label>
            ))}
          </div>
          <p className="font-mono text-sm text-text-secondary mt-3">Zapnuté probe modely sa ponúkajú návštevníkovi ako engine (Gemini / GPT), z ktorého si vyberá. Vypnutím engine z ponuky zmizne.</p>
        </section>
      )}
      </>
      )}
    </main>
  );
}
